import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import InstallAppButton from "@/components/InstallAppButton";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/command-center", label: "Command Center" },
  { to: "/schedule", label: "Schedule" },
  { to: "/roster", label: "Roster" },
  { to: "/stats", label: "Stats" },
  { to: "/breaking-news", label: "Breaking News" },
  { to: "/analysis", label: "Analysis" },
  { to: "/film-room", label: "Film Room" },
  { to: "/offseason", label: "Offseason" },
  { to: "/community", label: "Fan Zone" },
  { to: "/about", label: "About" },
];

const LOGO_URL = "/titan-standard-logo.webp";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl border-b border-[rgba(138,141,143,0.15)] transition-all duration-300 ${
        scrolled ? "bg-titans-void shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)]" : "bg-titans-void/85"
      }`}
    >
      <div
        className={`max-w-[1400px] mx-auto px-5 md:px-8 flex items-center justify-between transition-all duration-300 ${
          scrolled ? "h-[58px]" : "h-[74px]"
        }`}
      >
        <Link to="/" data-testid="brand-link" className="flex items-center group">
          <img
            src={LOGO_URL}
            alt="The Titan Standard"
            className={`w-auto object-contain crisp transition-all duration-300 ${
              scrolled ? "h-12 md:h-[52px]" : "h-16 md:h-[68px]"
            }`}
          />
          <span className="sr-only">The Titan Standard</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => {
            const isBreaking = n.to === "/breaking-news";
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-cond uppercase tracking-wider text-sm px-3 py-2 transition-colors whitespace-nowrap ${
                    isBreaking ? "nav-breaking" : ""
                  } ${
                    isActive ? "text-white border-b-2 border-titans-red" : (isBreaking ? "" : "text-titans-silver hover:text-white")
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <InstallAppButton variant="compact" />
          <button
            data-testid="header-search-btn"
            className="text-titans-silver hover:text-white p-2"
            aria-label="Search"
          >
            <Search size={18} />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[rgba(138,141,143,0.15)] bg-titans-navy" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block font-cond uppercase tracking-wider px-6 py-3 border-b border-[rgba(138,141,143,0.1)] ${
                  isActive ? "text-white bg-titans-elev border-l-4 border-l-titans-red" : "text-titans-silver"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
          <div className="px-6 py-4 border-b border-[rgba(138,141,143,0.1)]">
            <InstallAppButton variant="compact" />
          </div>
        </div>
      )}
    </header>
  );
}
